import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, XCircle, ChevronDown, ChevronRight, Hexagon } from "lucide-react";
import { api, fmtDateTime } from "@/lib/api";
import { BentoCard } from "@/components/dashboard/BentoCard";
import { HexBadge } from "@/components/dashboard/Hex";
import { RUNS } from "@/constants/testIds";

export default function RunsPage() {
  const [expanded, setExpanded] = useState(null);
  const { data: runs = [], isLoading } = useQuery({
    queryKey: ["runs"],
    queryFn: async () => (await api.get("/runs")).data,
    refetchInterval: 15000,
  });

  return (
    <div data-testid={RUNS.root} className="space-y-6">
      <div>
        <div className="data-label">History</div>
        <h1 className="font-display text-4xl font-bold tracking-tight">Runs</h1>
        <p className="text-sm text-zinc-600 mt-2">
          Every scrape ever executed, most recent first.
        </p>
      </div>

      {isLoading && <p className="text-zinc-500">Loading runs…</p>}

      {!isLoading && runs.length === 0 && (
        <BentoCard className="text-center py-12">
          <Hexagon size={48} className="mx-auto text-yellow-400" strokeWidth={1.5} />
          <p className="mt-3 font-display text-lg font-semibold">No runs yet</p>
          <p className="text-sm text-zinc-500">Trigger your first run from the Dashboard.</p>
        </BentoCard>
      )}

      <div className="space-y-3">
        {runs.map((run) => {
          const isExpanded = expanded === run.id;
          const tone = run.fail_count === 0 ? "success" : run.ok_count === 0 ? "error" : "warning";
          const badgeLabel = run.fail_count === 0 ? "OK" : run.ok_count === 0 ? "Failed" : "Partial";
          return (
            <div
              key={run.id}
              data-testid={RUNS.row(run.id)}
              className="bg-white border-2 border-black"
            >
              <button
                data-testid={`run-row-expand-${run.id}`}
                onClick={() => setExpanded(isExpanded ? null : run.id)}
                className="w-full flex items-center justify-between px-5 py-4 hover:bg-yellow-50 transition-colors"
              >
                <div className="flex items-center gap-4">
                  {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                  <HexBadge tone={tone}>{badgeLabel}</HexBadge>
                  <span className="font-mono-data text-sm text-zinc-700">
                    {fmtDateTime(run.started_at)}
                  </span>
                </div>
                <div className="flex items-center gap-6 text-sm">
                  <Stat label="Sources" value={`${run.ok_count}/${run.source_count}`} />
                  <Stat label="Records" value={run.property_count} />
                  <Stat label="Errors" value={run.fail_count} tone={run.fail_count > 0 ? "error" : ""} />
                </div>
              </button>
              {isExpanded && (
                <div className="border-t-2 border-black bg-zinc-50 px-5 py-4">
                  <div className="data-label mb-3">Per-source detail</div>
                  <div className="space-y-2">
                    {(run.runs || []).map((entry) => (
                      <div key={entry.source_id} className="flex items-start gap-3 py-2 border-b border-zinc-200 last:border-none">
                        {entry.ok ? (
                          <CheckCircle2 size={16} className="text-emerald-600 mt-0.5" />
                        ) : (
                          <XCircle size={16} className="text-red-500 mt-0.5" />
                        )}
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="font-mono-data font-semibold">{entry.source_id}</span>
                            <span className="text-xs text-zinc-500">· {entry.adapter}</span>
                          </div>
                          {entry.error ? (
                            <div className="mt-1 text-xs text-red-700 font-mono-data break-all">
                              {entry.error}
                            </div>
                          ) : (
                            <div className="mt-1 text-xs text-zinc-600">
                              {entry.record_count ?? entry.metadata?.row_count ?? 0} records
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Stat({ label, value, tone }) {
  return (
    <div className="text-right">
      <div className="data-label">{label}</div>
      <div className={`font-display font-bold ${tone === "error" ? "text-red-600" : ""}`}>
        {value}
      </div>
    </div>
  );
}
